<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useChatStore } from '../../stores/chatStore';
import { useSessionStore } from '../../stores/sessionStore';
import { useMatterStore } from '../../stores/matterStore';
import type { OptimisticSend } from '../../stores/chatStore';
import ContextMenu, { type ContextMenuItem } from '../common/ContextMenu.vue';

// Single chat input used in two presentations:
//  - "docked": the bottom bar shown once a session is selected (banners,
//    clarification, error inline).
//  - "hero": the large centered "Where should we start?" box on the
//    new-dialogue page, which has no session yet.
// The send path is unified: when there's no current session we create one
// (with an optimistic progress bubble) before sending, which is what the
// hero case needs; the docked case always has a session so it skips that.
const props = withDefaults(defineProps<{ variant?: 'hero' | 'docked' }>(), {
    variant: 'docked',
});
const isHero = computed(() => props.variant === 'hero');

const { t } = useI18n();
const router = useRouter();
const chatStore = useChatStore();
const sessionStore = useSessionStore();
const matterStore = useMatterStore();
const message = ref('');
const error = ref<string | null>(null);
const inputTextarea = ref<HTMLTextAreaElement | null>(null);
const clarificationAnswer = ref('');
const clarificationInputRef = ref<HTMLInputElement | null>(null);

const bannerContextMenu = ref<{ x: number; y: number } | null>(null);

const onBannerContextMenu = (event: MouseEvent) => {
    // Hold ctrl/cmd to bypass us and get the native browser menu.
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    bannerContextMenu.value = { x: event.clientX, y: event.clientY };
};

const closeBannerContextMenu = () => {
    bannerContextMenu.value = null;
};

const bannerMenuItems = computed<ContextMenuItem[]>(() => [
    {
        label: t('chat.debugMatterContext'),
        icon: 'bug_report',
        onSelect: () => chatStore.fetchMatterContextDebug(),
    },
]);

const focusInput = async () => {
    await nextTick();
    inputTextarea.value?.focus();
};

watch(
    () => sessionStore.currentSessionId,
    (newSessionId) => {
        message.value = '';
        if (newSessionId) focusInput();
    },
);

onMounted(() => {
    focusInput();
});

watch(
    () => chatStore.pendingClarification,
    async (val) => {
        if (val) {
            clarificationAnswer.value = '';
            await nextTick();
            if (clarificationInputRef.value) {
                clarificationInputRef.value.focus();
            }
        }
    },
);

const sendMessage = async () => {
    if (!message.value.trim() || chatStore.sending) {
        return;
    }

    const messageText = message.value.trim();

    try {
        error.value = null;
        let seed: OptimisticSend | undefined;
        if (!sessionStore.currentSession) {
            // No session yet (hero / new-dialogue case): seed the progress
            // bubble before the session round-trip so the user isn't staring
            // at a frozen input while the session is created, then sendMessage
            // reuses the seed instead of re-seeding.
            seed = chatStore.beginOptimisticSend(messageText);

            const sessionName =
                messageText.length > 60 ? messageText.substring(0, 60) + '...' : messageText;

            try {
                const newSession = await sessionStore.createSession(sessionName);
                sessionStore.selectSession(newSession);

                // Reflect the new session in the URL so it's bookmarkable /
                // copyable; until now we stayed on the matter landing route.
                const matterId = matterStore.currentMatter?.RecordId;
                if (matterId) {
                    router.push({
                        name: 'session',
                        params: { matterId, sessionId: newSession.RecordId },
                    });
                }
            } catch (err) {
                chatStore.cancelOptimisticSend(seed);
                throw err;
            }
        }

        await chatStore.sendMessage(messageText, seed);
        message.value = '';
    } catch (err) {
        error.value = err instanceof Error ? err.message : String(err);
        chatStore.sending = false;
    }
};

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
    }
};

const submitClarification = async () => {
    if (!clarificationAnswer.value.trim()) return;
    try {
        await chatStore.answerClarification({ answer: clarificationAnswer.value.trim() });
        clarificationAnswer.value = '';
    } catch (err) {
        error.value = err instanceof Error ? err.message : String(err);
    }
};

const skipClarification = async () => {
    // An empty answer signals "skip / I don't know": the backend sets the
    // question aside (planning) or records no data (disambiguation) rather
    // than re-asking.
    try {
        await chatStore.answerClarification({ answer: '' });
        clarificationAnswer.value = '';
    } catch (err) {
        error.value = err instanceof Error ? err.message : String(err);
    }
};

const handleClarificationKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submitClarification();
    }
};

const showMatterContextBanner = computed(() => {
    const status = chatStore.matterContextStatus as {
        has_run?: boolean;
        status?: string;
        completed_at?: string | null;
    } | null;
    if (!status?.has_run || status.status !== 'completed' || !status.completed_at) {
        return false;
    }
    // Matter context must be published before any dialog can be created, so on
    // a fresh matter the context is always older than every dialog. Only treat
    // it as an "update" — and show the banner — when the context was saved
    // after at least one displayed dialog was created.
    const completedAt = new Date(status.completed_at).getTime();
    return chatStore.chats.some((chat) => new Date(chat.Timestamp).getTime() < completedAt);
});
</script>

<template>
    <div class="chat-input" :class="{ 'chat-input--hero': isHero }">
        <template v-if="!isHero">
            <div
                v-if="showMatterContextBanner"
                class="matter-context-banner"
                @contextmenu="onBannerContextMenu"
            >
                {{ t('chat.matterDataUpdated') }}
            </div>

            <ContextMenu
                :position="bannerContextMenu"
                :items="bannerMenuItems"
                @close="closeBannerContextMenu"
            />

            <div v-if="chatStore.pendingClarification" class="clarification-banner">
                <div class="clarification-banner-header">
                    <span class="material-icons" style="font-size: 18px">help_outline</span>
                    <strong>{{ t('chat.clarificationNeeded') }}</strong>
                </div>
                <p class="clarification-banner-question">
                    {{ chatStore.pendingClarification.question }}
                </p>

                <div class="clarification-input-row">
                    <input
                        ref="clarificationInputRef"
                        v-model="clarificationAnswer"
                        type="text"
                        :placeholder="t('chat.clarificationPlaceholder')"
                        class="clarification-input"
                        @keydown="handleClarificationKeydown"
                    />
                    <button
                        class="clarification-submit"
                        :disabled="!clarificationAnswer.trim()"
                        @click="submitClarification"
                    >
                        <span class="material-icons">send</span>
                    </button>
                    <button
                        class="clarification-skip"
                        type="button"
                        @click="skipClarification"
                    >
                        {{ t('chat.clarificationSkip') }}
                    </button>
                </div>
            </div>
        </template>

        <div v-if="error" class="chat-input-error">{{ error }}</div>
        <div class="chat-input-container">
            <div class="chat-input-textarea-wrap">
                <textarea
                    ref="inputTextarea"
                    v-model="message"
                    :placeholder="
                        sessionStore.loading
                            ? ''
                            : isHero
                              ? t('chat.heroPlaceholder')
                              : t('chat.dockedPlaceholder')
                    "
                    class="chat-input-textarea"
                    :rows="isHero ? 4 : 3"
                    :disabled="
                        chatStore.sending ||
                        (!isHero && !!chatStore.pendingClarification) ||
                        sessionStore.loading
                    "
                    @keydown="handleKeydown"
                ></textarea>
                <div
                    v-if="sessionStore.loading"
                    class="chat-input-loading chat-input-loading-overlay"
                >
                    <div class="loading-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <span class="chat-input-loading-label">{{ t('chat.loadingSessions') }}</span>
                </div>
            </div>
            <div class="chat-input-actions">
                <button
                    class="chat-input-send-button"
                    :disabled="
                        !message.trim() ||
                        chatStore.sending ||
                        (!isHero && !!chatStore.pendingClarification) ||
                        sessionStore.loading
                    "
                    @click="sendMessage"
                >
                    <span class="material-icons">arrow_upward</span>
                    {{ t('chat.send') }}
                </button>
            </div>
        </div>
    </div>
</template>
